import React, { useEffect, useCallback } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import {
  BodyContent,
  Description,
  DescriptionButtonContainer,
  DescriptionContainer,
  DescriptionContentWhithGroups,
  DescriptionContentWhithoutGroups,
  DescriptionText,
  DescriptionText1,
  DescriptionTitle,
  LabelText,
  TableContainer,
  TitleContainer,
} from "./style";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { t } from "i18next";
import { ChannelContext } from "../../../context/channel";
import { dashboardApi } from "../../../api/DashboardApi";
import PageTitle from "../../shared/pageTitle/PageTitle";
import Label from "../../shared/label/Label";
import { DOMAIN_URL } from "../../../settings";
import Button from "../../shared/button/Button";
import colors from "../../../theme/colors";
import SixDotsIcon from "../../shared/Icons/SixDotsIcon";
import Toggle from "../../shared/Toggle";
import Input from "../../shared/input/Input";
import { Select } from "../../shared/InputSelect/style";

export type GroupData = {
  id?: number;
  name?: string;
  selected?: boolean;
  slug?: string;
  talks?: number;
  order?: number;
};

const BasicInformation = () => {
  const dashboardContext = React.useContext(ChannelContext);

  const breakpoints = useBreakpoint();

  const fillGroups = useCallback(() => {
    dashboardApi
      .listGroups(dashboardContext.channel)
      .then((response: { groups: any }) => {
        dashboardContext.setDraggableGroups([...response.groups]);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardContext.channel]);

  useEffect(() => {
    fillGroups();
  }, [fillGroups]);

  const reorder = (list: any, startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }
    const items: any = reorder(
      dashboardContext.draggableGroups,
      result.source.index,
      result.destination.index
    );
    dashboardContext.setDraggableGroups(items);
  };

  const handleToggle = (checked: boolean, group: GroupData) => {
    let update = [...dashboardContext.draggableGroups];
    let index = update.findIndex((e) => e.id === group.id);
    update[index].selected = checked;

    dashboardContext.setDraggableGroups([...update]);
  };

  return (
    <BodyContent>
      <PageTitle>{t('dashboard.basicInformation.title')}</PageTitle>
      <TitleContainer>
        <Label>{t('title')}</Label>
        <Input
          placeholder={dashboardContext.title}
          value={dashboardContext.title}
          onChange={(e: any) => {
            dashboardContext.setTitle(e.target.value);
          }}
        />
      </TitleContainer>
      <TitleContainer>
        <Label>{t('orderOfTalks')}</Label>
        <Select
          style={{width:'100%', maxWidth: '400px'}}
          placeholder={dashboardContext.orderOfTalks}
          value={dashboardContext.orderOfTalks}
          defaultValue={dashboardContext.orderOfTalks}
          onChange={(e: any) => {
            dashboardContext.setOrderOfTalks(e.target.value);
          }}
        >
          <option value="ASC">{t('orderOfTalks.asc')}</option>
          <option value="DESC">{t('orderOfTalks.desc')}</option>
          <option value="PLAYLIST">{t('orderOfTalks.playlist')}</option>
        </Select>
      </TitleContainer>
      <DescriptionContainer>
        {dashboardContext.draggableGroups.length < 0 ? (
          <DescriptionContentWhithoutGroups>
            <Label>{t('content')}</Label>
            <Description>
              <DescriptionTitle>
                {t('dashboard.basicInformation.noGroups.title')}
              </DescriptionTitle>
              <DescriptionText>
                {t('dashboard.basicInformation.noGroups.text')}
              </DescriptionText>
              <DescriptionButtonContainer>
                <a
                  href={`${DOMAIN_URL}/dashboard/groups/`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button
                    enable
                    variant="outlined"
                    colorType="white"
                    colorFont="body"
                    style={{
                      borderRadius: "40px",
                      whidth: "180px",
                      height: "46px",
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    {t('dashboard.basicInformation.createGroup')}
                  </Button>
                </a>
              </DescriptionButtonContainer>
            </Description>
          </DescriptionContentWhithoutGroups>
        ) : (
          <DescriptionContentWhithGroups>
            <LabelText>
              <Label>{t('content')}</Label>
              <DescriptionText1>
                {t('dashboard.basicInformation.content')}
              </DescriptionText1>
            </LabelText>
            {dashboardContext.draggableGroups.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                }}
              >
                <div style={{ marginTop: "40px" }}>
                  <LabelText>
                    <DescriptionText1>
                      {t('dashboard.basicInformation.noGroupsFound')}
                    </DescriptionText1>
                  </LabelText>
                </div>
              </div>
            ) : (
              <TableContainer>
                <div className="table">
                  <div className="thead">
                    <div className="tr-head">
                      <div className="th-group">{t('group')}</div>
                      <div className="th-video">{t('dashoboard.basicInformation.associatedContent')}</div>
                      <div className="th-display">{t('display')}</div>
                    </div>
                  </div>
                  <div className="tbody">
                    <DragDropContext onDragEnd={onDragEnd}>
                      <Droppable droppableId="droppable">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            {provided.placeholder}
                            {dashboardContext.draggableGroups.map(
                              (group: GroupData, index) => {
                                if (group.talks === 0) return "";
                                return (
                                  <Draggable
                                    key={group.id}
                                    draggableId={`${group.id}`}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        <div className="tr-container">
                                          <div className="td-group">
                                            <SixDotsIcon />
                                            <span className="group-title">
                                              {group.name}
                                            </span>
                                          </div>
                                          <div className="td-video">
                                            <span
                                              style={{
                                                textAlign: "center",
                                                fontSize: "10px",
                                              }}
                                            >{t('dashboard.basicInformation.countVideos', {count: group.talks})}</span>
                                          </div>
                                          <div className="td-display">
                                            <Toggle
                                              group={group}
                                              onChange={handleToggle}
                                              defaultText={
                                                breakpoints.md
                                                  ? t('no')!
                                                  : undefined
                                              }
                                              checkedText={
                                                breakpoints.md
                                                  ? t('yes')!
                                                  : undefined
                                              }
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              }
                            )}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </div>
                </div>
              </TableContainer>
            )}
          </DescriptionContentWhithGroups>
        )}
      </DescriptionContainer>
    </BodyContent>
  );
};

export default BasicInformation;
